import { Component, Input } from "@angular/core";
import { ColumnDataType } from "@app/main/components/reports/enums/ColumnDataType";
import { ReportDataFunction } from "@app/main/components/reports/enums/ReportDataFunction";
import { MeasurementCategoryType } from "@app/main/components/reports/enums/ReportMeasurementType";
import { IDashboardWidget } from "@app/main/modules/dashboard/_models/IDashboardWidget";
import { TranslationService } from "@app/services/translation.service";
import { IUnitConversionOptions, UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { DashboardService } from "../../dashboard.service";
import "./metric-widget.component.scss";

@Component({
    selector: "metric-widget-v3",
    template: require("./metric-widget.component.html")
})
export class MetricWidgetComponent {
    @Input() widget: IDashboardWidget;

    displayValue: any;
    unitLabel: string;
    errorLabel: string;

    trend: number;

    constructor(
        private readonly dashboardService: DashboardService,
        private readonly unitConversionService: UnitConversionService,
        private readonly translationService: TranslationService) {
    }

    ngOnInit(): void {
        this.setup();
    }

    private setup() {
        if (this.trend % 2 == 0) {
            this.trend = -this.trend;
        }

        this.widget.onDataDefinitionUpdated = () => {
            setTimeout(() => {
                this.updateDisplay();
            }, 0);
        };
    }

    private updateDisplay() {
        var results = this.dashboardService.convertDataDefinitionToMetricSeries(this.widget.dataDefinition);

        if (this.widget.processFailed) {
            this.errorLabel = this.translationService.t("Common.Unavailable");
            this.displayValue = null;
            this.trend = null;
            return;
        }

        if (results.length == 0) {
            this.displayValue = 0;
            this.trend = 0;
            return;
        }

        const firstHeader = this.widget.DataWidgetParameters.DataSource[0];
        if (firstHeader.DataSourceValue == "FuelEfficiency") {
            const unitConversionType = this.getUnitType();
            results.forEach(res => {
                res.value = this.unitConversionService.convert(res.value, unitConversionType, 1);
            })
        }
        const dataFunction =
            firstHeader.ColumnDefinition.Type == ColumnDataType.Percent || firstHeader.ColumnDefinition.MeasurementCategory == MeasurementCategoryType.FuelEfficiency
                ? ReportDataFunction.Average
                : firstHeader.DataFunction;

        this.widget.MetricWidgetParameters.displayValue = this.dashboardService.aggregateChartSeries(results, dataFunction, false);

        // Scoring should not have overall figure
        if (firstHeader.ColumnDefinition.Type == ColumnDataType.Score) {
            this.widget.MetricWidgetParameters.displayValue = null;
        }

        this.trend = this.calculateTrendPercentage(results.map(r => r.value));

        this.updateDisplayValue();
    }

    private updateDisplayValue() {
        const value = this.widget.MetricWidgetParameters.displayValue;

        const unitConversionType = this.getUnitType();
        if (this.unitConversionService.isLabelSuffix(unitConversionType)) {
            this.unitLabel = this.unitConversionService.getConversionLabel(unitConversionType);
        }

        const options: IUnitConversionOptions = {
            scaleLargeNumbers: false,
            precision: 1,
            dataType: this.getDataType(),
            dontAppendUnit: true
        };

        if (value == null) {
            this.displayValue = null;
            return;
        }

        if (this.widget.DataWidgetParameters.DataSource[0].DataSourceValue == "FuelEfficiency") {

            this.displayValue = this.unitConversionService.roundToXDecimalPlaces(value, 1);
        }
        else {
            this.displayValue = this.unitConversionService.smartConvert(value, unitConversionType, options);
        }
    }

    private getUnitType(): UnitConversionType {
        var header = this.widget.DataWidgetParameters.DataSource[0];
        return this.unitConversionService.convertMeasurementCategoryIntoUnitConversionType(header.ColumnDefinition.MeasurementCategory);
    }

    private getDataType(): ColumnDataType {
        var header = this.widget.DataWidgetParameters.DataSource[0];
        return header.ColumnDefinition.Type;
    }

    private calculateTrendPercentage(data: number[]): number {
        if (data.length < 2) {
            return 0;
        }

        let totalChange = 0;

        for (let i = 1; i < data.length; i++) {
            const change = data[i] - data[i - 1];
            totalChange += change;
        }

        const averageChange = totalChange / (data.length - 1);

        // Calculate trend percentage
        const trendPercentage = (averageChange / Math.abs(data[0])) * 100;

        return trendPercentage;
    }
}
